/**
 * 32 - Prison Break (10 §2): no insertion. He starts in the cell block of a wired camp with empty pouches
 * and the knife; the door is ajar, and somebody has cut the wire behind the block. His pods fell outside
 * the wire, south of the camp: reach them, arm up, and extract as on any other day. When the cell is found
 * empty the whole garrison goes to full alarm and comes to look - on a clock the HUD shows from the first
 * second, and which an alarm before then cuts in half.
 *
 * The camp lays its own wire (the spec's perimeter is 'none') so that the cut can be where the cell block
 * is; the way out - cell, cut, pods, pickup - is kept out of every posted man's sight (helpers3bEscape.ts),
 * which is what the quiet-path gate checks for a captured start (validate/quiet/legs.ts). The cut itself
 * carries a walk-through marker, because the orders send him to find it (ROUND5 §2.2).
 */

import { PI } from '../../sim/math';
import { buildColumn } from '../compound';
import { FLAG } from '../grid';
import { buildRun } from '../obstacles';
import { COMPOUND, LINES } from '../tuning';
import type { Point } from '../types';
import { checker, countKind, insideRect, required, structure } from './helpers';
import { flagTargets, quietDir, RECALL, squareAt } from './helpers3b';
import { blindGuard, reserveWay, sightNow } from './helpers3bEscape';
import { crawlSeconds, PACE, timedRule, walkSeconds } from './helpers3bTime';
import type { ArchetypeSpec } from './types';

const CUT_WIDTH = 3;
const POD_ZONE = 4;
/** The pods lie this far south of the wire, and this far apart. */
const PODS_SOUTH = 9;
const POD_SPACING = 3.5;

/** Camp guards posted by the mission itself: three at threat 1, six at threat 9. */
export const campGuards = (threat: number): number => 3 + Math.floor(threat / 3);

export const prisonBreak: ArchetypeSpec = {
  id: 'prison-break',
  name: 'PRISON BREAK',
  nativeBiome: 'arctic',
  baseThreat: 6,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'none',
  sealed: false,
  layout: 'compound',
  size: { w: [50, 58], d: [30, 34] },
  river: false,
  patrolLoop: 'outside',
  breach: { min: 0, max: 0 },

  build(g) {
    const c = g.rect;
    const q = quietDir(g);
    const flankX = q > 0 ? c.x1 : c.x0;
    // The cell block: the north corner on the quiet side, its back a few metres from the wire.
    const cell = { x: Math.floor(flankX - q * 10) + 0.5, z: Math.floor(c.z0 + 9) + 0.5 };

    // The wire: a gate where the road comes in, and the cut beside the cell block.
    const half = COMPOUND.columnPiece / 2;
    const gateX = g.ctx.plan.roadEnd.x;
    buildRun(g.map, 'wire', c.z1, c.x0 - half, c.x1 + half, [{ x: gateX, width: COMPOUND.gateWidth }]);
    buildRun(g.map, 'wire', c.z0, c.x0 - half, c.x1 + half, []);
    for (const x of [c.x0, c.x1]) buildColumn(g.map, 'wire', x, c.z0 + 0.7, c.z1 - 0.7, x === flankX ? [{ x: cell.z, width: CUT_WIDTH }] : []);
    g.map.mask.circle(gateX, c.z1, COMPOUND.gateWidth / 2 + 1.5, FLAG.keep);
    g.map.road([[gateX, c.z1 + 1], [gateX, c.z1 - 7]], LINES.roadWidth);

    g.map.add('cellBlock', cell.x, cell.z);
    // ROUND5 §2.2 (the marker audit): "FIND THE GAP AND GO THROUGH IT" - and nothing on the map said where the
    // gap was. A walk-through marker stands in the cut; it is flagged in `finish`, because a prop flagged
    // during the build draws a sentry post (posts.ts), and a man watching the cut is the one thing this
    // mission cannot have.
    const cut = g.map.add('overlookMarker', flankX, cell.z);
    // His pods: good ground outside the wire, south of the camp on the quiet side.
    const podsAt: Point[] = [];
    for (let i = 0; i < 3; i++) {
      const spot = g.map.nearestClear(flankX + q * (5 + i * POD_SPACING), c.z1 + PODS_SOUTH, 1.2, 6, FLAG.solid | FLAG.trench | FLAG.water | FLAG.mine | FLAG.road);
      if (spot) podsAt.push({ x: spot[0], z: spot[1] });
    }
    const podA = podsAt[0];
    if (!podA) g.map.problems.push('prison-break: no good ground for the pods outside the wire');
    const pods = podA ?? { x: flankX + q * 5, z: c.z1 + PODS_SOUTH };
    for (const p of podsAt) g.map.mask.circle(p.x, p.z, 2, FLAG.keep);

    // The way out, kept out of every posted man's sight: cell, cut, down the outside of the wire, pods, pickup.
    const outside = flankX + q * 4.5;
    const pickup = g.ctx.plan.pickup;
    const way = reserveWay(g, [cell, { x: outside, z: cell.z }, { x: outside, z: pods.z }, pods, { x: pods.x, z: pickup.z }, pickup]);
    g.ctx.quiet.exposed.push(...way);
    // From here on the map is planned round a man who starts in the cell: the garrison's stand-off, the fallen ranger's grave.
    g.ctx.plan.player = { ...cell };

    // The camp, on the far side of the block from the cut: guard house, huts, stores.
    const yard = q > 0
      ? { x0: c.x0 + 4, z0: c.z0 + 4, x1: cell.x - 8, z1: c.z1 - 4 }
      : { x0: cell.x + 8, z0: c.z0 + 4, x1: c.x1 - 4, z1: c.z1 - 4 };
    const placed: Point[] = [];
    const house = required(g, 'guardHouse', { within: yard, from: placed, minDist: 0, margin: 1.4 });
    for (let i = 0; i < 2; i++) structure(g, 'hut', { within: yard, minDist: 9, from: placed, margin: 1.4 });
    structure(g, 'alarmPost', { within: yard, margin: 1 });
    for (let i = 0; i < 3; i++) structure(g, 'crate', { within: yard, margin: 0.5 });

    const eyes = sightNow(g);
    const kind = g.elite ? 'elite' : 'rifleman';
    // The warder at the block's door (its south face), the man at the guard house, and the rest about the yard.
    const posts: Array<[number, number, number]> = [[cell.x - q * 1.5, cell.z + 5, 0], [house.x, house.z + 3.2, 0]];
    for (let i = posts.length; i < campGuards(g.threat); i++) {
      posts.push([g.rng.range(yard.x0, yard.x1), g.rng.range(yard.z0, yard.z1), g.rng.range(-0.8, 0.8) + (q > 0 ? -PI / 2 : PI / 2)]);
    }
    let posted = 0;
    for (const [x, z, facing] of posts) if (blindGuard(g, eyes, way, x, z, facing, kind)) posted++;
    if (posted < 2) g.map.problems.push('prison-break: the camp has nowhere to post its guards out of sight of the cut');

    const zone = squareAt(pods.x, pods.z, POD_ZONE);
    return {
      objective: {
        kind: 'visitZones', label: 'REACH YOUR PODS — OUTSIDE THE WIRE, SOUTH OF THE CAMP', targetPropIds: [], minFraction: 1,
        zones: [zone], dwellSeconds: 0,
        rules: [
          {
            type: 'timedEvent', atSeconds: 120, effect: 'cellFoundEmpty', hastenAtAlert: 2,
            orders: 'THE WIRE IS CUT BEHIND THE CELL BLOCK. WHEN THEY FIND THE CELL EMPTY THE WHOLE CAMP COMES LOOKING - AN ALARM BRINGS IT FORWARD',
            warning: 'THE CELL IS FOUND EMPTY IN',
            reason: 'THE CELL WAS FOUND EMPTY',
          },
          RECALL,
        ],
      },
      mission: { startCaptured: true, podsAt, player: { x: cell.x, z: cell.z, facing: q > 0 ? PI / 2 : -PI / 2 } },
      finish(def) {
        flagTargets(def, [cut.id]);
        const rule = timedRule(def, 'cellFoundEmpty');
        if (rule) rule.atSeconds = crawlSeconds(def, def.player, pods);
      },
    };
  },

  asserts(def, probe) {
    const { check, out } = checker('prison-break');
    const o = def.objective;
    check(def.startCaptured === true, 'he must start captured');
    const block = def.props.find((p) => p.kind === 'cellBlock');
    check(block !== undefined && countKind(def, 'cellBlock') === 1, 'needs exactly one cell block');
    if (block) {
      check(Math.abs(def.player.x - block.x) <= 3.5 && Math.abs(def.player.z - block.z) <= 2.5, 'he must start inside the cell block');
      check(insideRect(def.goalZone, block.x, block.z), 'the cell block must stand inside the wire');
    }
    // ROUND5: the cut is the one thing the orders tell him to find. It is marked, it is beside the block,
    // it stands on the wire line, and it really is a gap - ground a man can walk out through.
    const cut = def.props.find((p) => p.kind === 'overlookMarker');
    check(cut !== undefined && countKind(def, 'overlookMarker') === 1, 'the cut in the wire needs exactly one marker');
    if (cut && block) {
      check(cut.target === true, 'the cut in the wire must be marked on the strip map');
      check(
        Math.abs(cut.x - def.goalZone.x0) < 0.6 || Math.abs(cut.x - def.goalZone.x1) < 0.6,
        'the cut must be in the wire down the side of the camp',
      );
      check(Math.abs(cut.z - block.z) <= 2, 'the cut must be behind the cell block');
      check(probe.standable(squareAt(cut.x, cut.z, 0.6)) >= 1, 'the marked cut must be a gap he can walk through');
    }
    const pods = def.podsAt ?? [];
    check(pods.length >= 1 && pods.length <= 3, 'his pods must lie on the ground');
    for (const p of pods) {
      check(!insideRect(def.goalZone, p.x, p.z) && p.z > def.goalZone.z1, 'the pods fell outside the wire, south of the camp');
    }
    const [zone] = o.zones ?? [];
    const [podA] = pods;
    check(o.kind === 'visitZones' && (o.zones?.length ?? 0) === 1 && (o.dwellSeconds ?? 0) === 0, 'objective: step into the one zone, at the pods');
    check(zone !== undefined && podA !== undefined && insideRect(zone, podA.x, podA.z), 'the zone to reach must hold pod A');
    const wire = def.props.filter((p) => p.kind === 'wire' && insideRect(def.goalZone, p.x, p.z));
    check(wire.length > 40, 'the camp must be wired');
    const rule = timedRule(def, 'cellFoundEmpty');
    check(rule !== undefined && rule.hastenAtAlert === 2, 'the cell must be found empty on a clock, which an alarm brings forward');
    check(
      rule?.orders !== undefined && rule.warning !== undefined && rule.reason !== undefined,
      'the rule must carry its orders, its countdown and its reason',
    );
    if (rule && podA) {
      // Walking, he must be able to reach the pods well inside the time - even once an alarm has halved it.
      const walk = walkSeconds(def, def.player, podA);
      check(rule.atSeconds * 0.5 >= walk, `the clock (${rule.atSeconds} s) must leave time to reach the pods even when hastened`);
      check(rule.atSeconds <= def.clockSeconds * PACE.clockShare + 5, 'the alarm must come while the mission is still on');
    }
    return out;
  },
};
